import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { finalize } from 'rxjs';
import { DropdownOption } from '@core/models/models';
import { DataSourceRegistryService } from '@core/services/data-source-registry.service';

export interface QuickCreateOptionResult {
  options: DropdownOption[];
  selectedValue: unknown;
}

@Component({
  selector: 'app-quick-create-option',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule],
  template: `
    <button
      type="button"
      class="quick-add-button"
      [disabled]="disabled"
      [attr.aria-label]="'Adicionar ' + entityLabel.toLowerCase()"
      [title]="disabled ? disabledHint : 'Adicionar ' + entityLabel.toLowerCase()"
      (click)="open()"
    >
      <i class="pi pi-plus" aria-hidden="true"></i>
    </button>

    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [closable]="!saving"
      [closeOnEscape]="!saving"
      [header]="'Adicionar ' + entityLabel"
      (onShow)="focusInput()"
      appendTo="body"
      styleClass="quick-create-dialog"
      [style]="{
        width: '420px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 48px)',
        margin: '24px 0'
      }"
    >
      <div class="quick-create-content">
        <div class="quick-create-icon">
          <i [class]="entityLabel === 'Marca' ? 'pi pi-bookmark' : 'pi pi-box'"></i>
        </div>
        <div>
          <strong>Nova {{ entityLabel.toLowerCase() }}</strong>
          <p>{{ helperText }}</p>
        </div>
      </div>

      <label class="quick-create-label" [for]="inputId">
        Nome <span>*</span>
      </label>
      <input
        [id]="inputId"
        #nameInput
        pInputText
        class="w-full"
        [(ngModel)]="name"
        [disabled]="saving"
        [placeholder]="'Ex.: ' + example"
        maxlength="120"
        (keyup.enter)="save()"
      />
      <small class="quick-create-error" *ngIf="submitted && !name.trim()">
        Informe o nome da {{ entityLabel.toLowerCase() }}.
      </small>

      <ng-template pTemplate="footer">
        <div class="quick-create-actions">
          <button
            type="button"
            class="quick-action quick-action--secondary"
            [disabled]="saving"
            (click)="visible = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="quick-action quick-action--primary"
            [disabled]="saving"
            [attr.aria-busy]="saving"
            (click)="save()"
          >
            <i [class]="saving ? 'pi pi-spinner pi-spin' : 'pi pi-check'" aria-hidden="true"></i>
            <span>{{ saving ? 'A adicionar...' : 'Adicionar e selecionar' }}</span>
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: inline-flex; flex: 0 0 auto; }
    ::ng-deep .quick-create-dialog .p-dialog-content {
      padding: 24px 20px 22px !important;
    }
    ::ng-deep .quick-create-dialog .p-dialog-footer {
      padding: 16px 20px 20px !important;
    }
    .quick-add-button {
      width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center;
      border: 1px solid rgba(59, 130, 246, .45); border-radius: 8px;
      background: rgba(59, 130, 246, .1); color: var(--sp-primary, #3b82f6); cursor: pointer;
      transition: background .15s ease, border-color .15s ease, transform .15s ease;
    }
    .quick-add-button:hover:not(:disabled) { background: rgba(59, 130, 246, .2); border-color: var(--sp-primary, #3b82f6); transform: translateY(-1px); }
    .quick-add-button:focus-visible { outline: 2px solid var(--sp-primary, #3b82f6); outline-offset: 2px; }
    .quick-add-button:disabled { opacity: .4; cursor: not-allowed; }
    .quick-create-content { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; padding: 12px; border: 1px solid var(--sp-border); border-radius: 9px; background: var(--sp-surface2); }
    .quick-create-icon { width: 38px; height: 38px; display: grid; place-items: center; flex: 0 0 38px; border-radius: 8px; color: var(--sp-primary); background: rgba(59, 130, 246, .12); }
    .quick-create-content strong { color: var(--sp-text); font-size: 14px; }
    .quick-create-content p { margin: 3px 0 0; color: var(--sp-muted); font-size: 12px; line-height: 1.4; }
    .quick-create-label { display: block; margin-bottom: 7px; color: var(--sp-text); font-size: 13px; font-weight: 600; }
    .quick-create-label span, .quick-create-error { color: var(--sp-danger, #ef4444); }
    .quick-create-error { display: block; margin-top: 6px; font-size: 12px; }
    .quick-create-actions { width: 100%; display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
    .quick-action {
      min-height: 40px; padding: 0 16px; display: inline-flex; align-items: center; justify-content: center;
      gap: 8px; flex: 0 0 auto; border-radius: 8px; font-size: 13px; font-weight: 600;
      line-height: 1; white-space: nowrap; cursor: pointer; transition: background .15s ease, border-color .15s ease, opacity .15s ease;
    }
    .quick-action--secondary {
      min-width: 92px; border: 1px solid var(--sp-border) !important;
      background: var(--sp-surface2) !important; color: var(--sp-muted) !important;
    }
    .quick-action--secondary:hover:not(:disabled) { color: var(--sp-text) !important; border-color: rgba(148, 163, 184, .5) !important; }
    .quick-action--primary {
      min-width: 190px; border: 1px solid var(--sp-primary, #3b82f6) !important;
      background: var(--sp-primary, #3b82f6) !important; color: #fff !important;
    }
    .quick-action--primary:hover:not(:disabled) { filter: brightness(1.08); }
    .quick-action:disabled { cursor: wait !important; opacity: .72 !important; }
    .quick-action span { display: inline-block; white-space: nowrap; }
    .quick-action .pi-spinner { animation: quick-create-spin .75s linear infinite; }
    @keyframes quick-create-spin { to { transform: rotate(360deg); } }
    @media (max-width: 430px) {
      .quick-create-actions { align-items: stretch; flex-direction: column-reverse; }
      .quick-action { width: 100%; min-width: 0; }
    }
  `],
})
export class QuickCreateOptionComponent {
  private static nextId = 0;
  private readonly registry = inject(DataSourceRegistryService);
  private readonly messageService = inject(MessageService);

  @Input({ required: true }) sourceKey = '';
  @Input({ required: true }) dependValue: number | null = null;
  @Input() disabled = false;
  @Input() existingOptions: DropdownOption[] = [];
  @Input() optionName = '';
  @Output() optionCreated = new EventEmitter<QuickCreateOptionResult>();

  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  readonly inputId = `quick-option-${++QuickCreateOptionComponent.nextId}`;
  visible = false;
  saving = false;
  submitted = false;
  name = '';

  get isPropertyOption(): boolean {
    return this.sourceKey === 'PROPRIEDADE_OPCOES';
  }

  get entityLabel(): string {
    if (this.isPropertyOption) return this.optionName.trim() || 'Opção';
    return this.sourceKey.startsWith('MARCAS_') ? 'Marca' : 'Modelo';
  }

  get helperText(): string {
    if (this.isPropertyOption) {
      return 'A nova opção ficará disponível apenas para esta propriedade.';
    }
    return this.entityLabel === 'Marca'
      ? 'A nova marca ficará disponível apenas na categoria deste equipamento.'
      : 'O novo modelo ficará associado à marca selecionada.';
  }

  get disabledHint(): string {
    if (this.isPropertyOption) return 'A propriedade ainda não está disponível.';
    return this.entityLabel === 'Modelo'
      ? 'Selecione primeiro uma marca.'
      : 'A categoria do equipamento ainda não está disponível.';
  }

  get example(): string {
    if (this.isPropertyOption) return 'Nova opção';
    return this.entityLabel === 'Marca' ? 'Toyota' : 'Hilux';
  }

  open(): void {
    if (this.disabled) return;
    this.name = '';
    this.submitted = false;
    this.visible = true;
  }

  focusInput(): void {
    setTimeout(() => this.nameInput?.nativeElement.focus());
  }

  save(): void {
    this.submitted = true;
    const name = this.name.trim();
    if (!name || this.dependValue == null || this.saving) return;

    const existing = this.existingOptions.find(option =>
      option.label.trim().localeCompare(name, undefined, { sensitivity: 'base' }) === 0,
    );
    if (existing) {
      this.optionCreated.emit({
        options: this.existingOptions,
        selectedValue: existing.value,
      });
      this.visible = false;
      this.messageService.add({
        severity: 'info',
        summary: `${this.entityLabel} já existente`,
        detail: `${existing.label} foi selecionada.`,
        life: 2500,
      });
      return;
    }

    this.saving = true;
    this.registry
      .createOption(this.sourceKey, this.dependValue, name)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: options => {
          const created = [...options]
            .reverse()
            .find(option => option.label.trim().localeCompare(name, undefined, { sensitivity: 'base' }) === 0);

          this.optionCreated.emit({
            options,
            selectedValue: created?.value ?? null,
          });
          this.visible = false;
          this.messageService.add({
            severity: 'success',
            summary: `${this.entityLabel} adicionada`,
            detail: created
              ? `${created.label} foi adicionada e selecionada.`
              : `${name} foi adicionada.`,
            life: 3000,
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: `Não foi possível adicionar a ${this.entityLabel.toLowerCase()}`,
            detail: err?.error?.message ?? 'Confirme os dados e tente novamente.',
            life: 4000,
          });
        },
      });
  }
}
